import axios from 'axios';

import store from '../redux/store/store';

import { resetCredentials, setCredentials } from '../redux/reducers/authReducer';
import { translateSupervisionError } from '../utils/supervisionErrors';

const HTTP_MESSAGES = {
  400: "Requête invalide.",
  401: "Session expirée. Veuillez vous reconnecter.",
  403: "Vous n'avez pas les droits pour cette action.",
  404: "Ressource introuvable.",
  408: "La requête a expiré.",
  422: "Données invalides.",
  429: "Trop de requêtes. Réessayez dans un moment.",
  500: "Erreur serveur. Réessayez plus tard.",
  502: "Serveur indisponible.",
  503: "Service temporairement indisponible.",
};

const BACKEND_TRANSLATIONS = {
  "invalid credentials": "Identifiants incorrects.",
  "invalid token": "Token invalide.",
  "token expired": "Session expirée. Veuillez vous reconnecter.",
  "token is expired": "Session expirée. Veuillez vous reconnecter.",
  "unauthorized": "Accès non autorisé.",
  "forbidden": "Vous n'avez pas les droits pour cette action.",
  "user not found": "Utilisateur introuvable.",
  "user already exists": "Cet utilisateur existe déjà.",
  "email already exists": "Cette adresse email est déjà utilisée.",
  "email already registered": "Cette adresse email est déjà utilisée.",
  "email already in use": "Cette adresse email est déjà utilisée.",
  "wrong password": "Mot de passe incorrect.",
  "incorrect password": "Mot de passe incorrect.",
  "password too short": "Le mot de passe est trop court.",
  "account disabled": "Ce compte est désactivé.",
  "account not found": "Compte introuvable.",
  "account already exists": "Ce compte existe déjà.",
  "field required": "Ce champ est obligatoire.",
  "invalid email": "Adresse email invalide.",
  "invalid format": "Format invalide.",
  "value is required": "Cette valeur est requise.",
  "not found": "Ressource introuvable.",
  "internal server error": "Erreur serveur. Réessayez plus tard.",
  "service unavailable": "Service temporairement indisponible.",
  "bad gateway": "Serveur indisponible.",
  "request timeout": "La requête a expiré.",
  "too many requests": "Trop de requêtes. Réessayez dans un moment.",
  "cannot delete admin": "Impossible de supprimer cet administrateur.",
  "access denied": "Accès refusé.",
  "insufficient permissions": "Droits insuffisants.",
};

const translateBackendMessage = (msg) => {
  if (!msg) return null;
  const lower = msg.toLowerCase().trim();
  if (BACKEND_TRANSLATIONS[lower]) return BACKEND_TRANSLATIONS[lower];
  const matchedKey = Object.keys(BACKEND_TRANSLATIONS).find((key) => lower.includes(key));
  return matchedKey ? BACKEND_TRANSLATIONS[matchedKey] : null;
};

const extractRawMsg = (data) => {
  if (!data) return null;
  if (typeof data === 'string' && data.length < 300) return data;
  const root = data.message || data.error || data.msg;
  if (root && typeof root === 'string') return root;
  if (typeof data.detail === 'string') return data.detail;
  if (Array.isArray(data.detail)) {
    const first = data.detail[0];
    if (first?.msg && typeof first.msg === 'string') return first.msg;
  }
  if (data.detail && typeof data.detail === 'object') {
    const inner = data.detail.message || data.detail.detail || data.detail.msg || data.detail.error;
    if (inner && typeof inner === 'string') return inner;
  }
  return null;
};

const getErrorMessage = (error) => {
  if (error?.response) {
    const { status, data } = error.response;
    const rawMsg = extractRawMsg(data);

    if (rawMsg) {
      // 1. D'abord les traductions précises (quota, supervision, droits, etc.)
      const specific = translateSupervisionError(rawMsg);
      if (specific) return specific;

      // 2. Ensuite le petit dictionnaire générique existant
      const translated = translateBackendMessage(rawMsg);
      if (translated) return translated;
    }

    // 3. Fallback par statut HTTP
    return HTTP_MESSAGES[status] || `Erreur ${status}.`;
  }

  if (error?.code === 'ECONNABORTED' || error?.message?.includes('timeout')) {
    return "La requête a expiré. Vérifiez votre connexion.";
  }

  if (error?.message === 'Network Error') return "Impossible de contacter le serveur.";

  return "Une erreur est survenue.";
};

// Partagés entre toutes les instances déclaré en dehors de createApiInstance() → éviter les refresh parallèles

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) prom.reject(error);
    else prom.resolve(token);
  });
  failedQueue = [];
};

const handleLogout = () => {
  store.dispatch(resetCredentials());
  if (window.location.pathname !== '/dashboard/login') {
    window.location.href = '/dashboard/login';
  }
};

const createApiInstance = (baseURL) => {
  const instance = axios.create({
    baseURL,
    headers: { 'Content-Type': 'application/json' },
    withCredentials: false,
  });

  instance.interceptors.request.use(
    (config) => {
      const token = store.getState().auth.accessToken;
      if (token) config.headers.Authorization = `Bearer ${token}`;
      return config;
    },
    (error) => Promise.reject(error),
  );

  instance.interceptors.response.use(
    (response) => {
      const newToken = response.headers['x-access-token'];
      if (newToken) {
        const currentToken = store.getState().auth.accessToken;
        if (newToken !== currentToken) {
          store.dispatch(setCredentials({ accessToken: newToken }));
        }
      }
      return response;
    },

    async (error) => {
      const originalRequest = error.config;
      // vérifier si refresh en cours → mettre requête en queue en attente
      if (error.response?.status === 401 && !originalRequest._retry) {
        const currentToken = store.getState().auth.accessToken;
        const requestToken = originalRequest?.headers?.Authorization?.replace('Bearer ', '');

        // Un appel parallèle a déjà refreshé → retry direct
        if (currentToken && requestToken && currentToken !== requestToken) {
          originalRequest._retry = true;
          originalRequest.headers.Authorization = `Bearer ${currentToken}`;
          return instance(originalRequest);
        }

        // Refresh déjà en cours → mettre en queue
        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          }).then(token => {
            originalRequest._retry = true;
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return instance(originalRequest);
          }).catch(err => Promise.reject(err));
        }

        isRefreshing = true;
        originalRequest._retry = true;

        try {
          // Attendre 500ms — le WS a peut-être déjà refreshé le token
          await new Promise(resolve => setTimeout(resolve, 500));

          const freshToken = store.getState().auth.accessToken;

          if (freshToken && freshToken !== requestToken) {
            // WS a déjà mis à jour le token → utiliser directement
            processQueue(null, freshToken);
            originalRequest.headers.Authorization = `Bearer ${freshToken}`;
            return instance(originalRequest);
          }

          // Pas de refresh WS → relancer la requête, le backend va refresher
          // et retourner le nouveau token dans x-access-token
          const response = await instance(originalRequest);
          const newToken = response.headers['x-access-token'];
          if (newToken) {
            store.dispatch(setCredentials({ accessToken: newToken }));
            processQueue(null, newToken);
          }
          return response;

        } catch (refreshError) {
          processQueue(refreshError, null);
          handleLogout();
          return Promise.reject(refreshError);
        } finally {
          isRefreshing = false;
        }
      }

      const readableMessage = getErrorMessage(error);
      const cleanError = new Error(readableMessage);
      cleanError.status = error?.response?.status;
      cleanError.data = error?.response?.data;
      cleanError.original = error;

      return Promise.reject(cleanError);
    },
  );

  return instance;
};

export const api = createApiInstance(process.env.REACT_APP_AUTH_API);

export const modelApi = createApiInstance(
  process.env.REACT_APP_MODEL_API ||
  process.env.REACT_APP_AUTH_API?.replace('/auth/v1', '/model-physic')
);

export default api;